/**
 * useLedgerEvents.tsx
 * React hook + example component to stream CapLedger ledger events in real time.
 */

import { useEffect, useState } from 'react';
import * as CapLedgerService from '@/services/capLedgerService';

// ------------------------------
// Hook: useLedgerEvents
// ------------------------------
export function useLedgerEvents(companyId?: string) {
  const [events, setEvents] = useState<CapLedgerService.LedgerEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let channel: ReturnType<typeof CapLedgerService.subscribeToLedgerEvents> | null = null;

    async function loadData() {
      try {
        setLoading(true);
        const data = await CapLedgerService.getLedgerEvents(companyId);
        setEvents(data);
      } catch (err) {
        console.error('Error loading ledger events:', err);
      } finally {
        setLoading(false);
      }
    }

    loadData();

    // Subscribe to real-time updates
    channel = CapLedgerService.subscribeToLedgerEvents((payload) => {
      console.log('📊 New ledger event:', payload);

      // Add new event to the beginning of the list
      if (payload.new && (!companyId || payload.new.company_id === companyId)) {
        setEvents((prev) => [payload.new as CapLedgerService.LedgerEvent, ...prev]);
      }

      // Handle updates
      if (payload.eventType === 'UPDATE' && payload.new) {
        setEvents((prev) =>
          prev.map((event) =>
            event.id === payload.new.id ? (payload.new as CapLedgerService.LedgerEvent) : event
          )
        );
      }

      // Handle deletes
      if (payload.eventType === 'DELETE' && payload.old) {
        setEvents((prev) => prev.filter((event) => event.id !== payload.old.id));
      }
    }, companyId);

    return () => {
      if (channel) {
        CapLedgerService.unsubscribeFromChannel(channel);
      }
    };
  }, [companyId]);

  return { events, loading };
}
